import { Text } from '@mantine/core';
import { useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { travelDurationDays } from '@/module/Travel/Request/TravelRequestFormUtils';
import { TravelRequestSchema } from '@/module/Travel/Request/model/TravelRequestSchema';

interface TravelDurationTextProps {
  // The server count over the trip dates, which also leaves out legal holidays.
  workingDays?: number | null;
}

export function TravelDurationText({ workingDays }: TravelDurationTextProps) {
  const { t } = useTranslation();
  const [departureDate, returnDate] = useWatch<TravelRequestSchema, ['departureDate', 'returnDate']>({
    name: ['departureDate', 'returnDate'],
  });

  const duration = travelDurationDays(departureDate, returnDate);
  if (duration == null) {
    return null;
  }

  return (
    <Text
      size="sm"
      c="dimmed"
    >
      {t('travel.requests.form.duration', { calendar: duration.calendar, working: workingDays ?? duration.working })}
    </Text>
  );
}

// The form validates on submit only, so a misplaced event period is said here as soon as it is typed.
export function TravelEventPeriodText() {
  const { t } = useTranslation();
  const [departureDate, returnDate, eventStartDate, eventEndDate] = useWatch<
    TravelRequestSchema,
    ['departureDate', 'returnDate', 'eventStartDate', 'eventEndDate']
  >({
    name: ['departureDate', 'returnDate', 'eventStartDate', 'eventEndDate'],
  });

  const reversed = !!eventStartDate && !!eventEndDate && eventEndDate < eventStartDate;
  const outsideTrip =
    (!!departureDate && !!eventStartDate && eventStartDate < departureDate) ||
    (!!returnDate && !!eventEndDate && eventEndDate > returnDate);
  if (!reversed && !outsideTrip) {
    return null;
  }

  return (
    <Text
      size="sm"
      c="dimmed"
    >
      {t(`travel.requests.form.${reversed ? 'durationInvalid' : 'durationOutsideTrip'}`)}
    </Text>
  );
}
